import React, { useState, useEffect, useRef } from 'react';
import { useBranding } from '../../context/BrandingContext';
import { compressImage } from '../../lib/utils';
import { customFieldsAPI, backupAPI, migrationAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Settings,
  Palette,
  Type,
  Image as ImageIcon,
  Code,
  Save,
  RotateCcw,
  Loader2,
  Upload,
  Eye,
  Wrench,
  Check,
  Download,
  FileUp,
  ToggleLeft,
  DollarSign,
  FileText,
  ClipboardList,
  Plus,
  Trash2,
  Edit3,
  X,
  Hash,
  Calendar,
  List,
  Database,
  HardDrive,
  AlertTriangle
} from 'lucide-react';
import Layout from '../../components/Layout';
import { TranslatableSpan } from '../../components/TranslatableText';

const PRESET_COLORS = {
  primary: ['#F97316', '#EF4444', '#3B82F6', '#22C55E', '#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B'],
  background: ['#09090B', '#0F172A', '#1E1B4B', '#0C4A6E', '#1A1A2E', '#0D1117'],
  card: ['#18181B', '#1E293B', '#312E81', '#0E7490', '#16213E', '#161B22']
};

const FONT_OPTIONS = [
  'Barlow Condensed',
  'Inter',
  'Roboto',
  'Open Sans',
  'Poppins',
  'Montserrat',
  'Oswald',
  'Raleway',
  'Lato',
  'Source Sans Pro'
];

const SettingsPage = () => {
  const { branding, updateBranding, resetBranding, loading: brandingLoading } = useBranding();
  const [settings, setSettings] = useState(branding);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);
  
  // Custom Fields state
  const [customFields, setCustomFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [newField, setNewField] = useState({
    name: '',
    field_type: 'text',
    options: [],
    required: false,
    description: ''
  });
  const [optionInput, setOptionInput] = useState('');

  useEffect(() => {
     
    setSettings(branding);
  }, [branding]);

     
  useEffect(() => {
    // Check if there are unsaved changes
    const changed = JSON.stringify(settings) !== JSON.stringify(branding);
    setHasChanges(changed);
  }, [settings, branding]);

     
  // Fetch custom fields on mount
  useEffect(() => {
    fetchCustomFields();
  }, []);

  const fetchCustomFields = async () => {
    setLoadingFields(true);
    try {
      const response = await customFieldsAPI.getAll();
      setCustomFields(response.data);
    } catch (err) {
      console.error('Failed to fetch custom fields:', err);
    } finally {
      setLoadingFields(false);
    }
  };

  const handleCreateField = async () => {
    if (!newField.name.trim()) {
      toast.error('Field name is required');
      return;
    }
    
    try {
      const response = await customFieldsAPI.create({
        ...newField,
        options: newField.field_type === 'dropdown' ? newField.options : null
      });
      setCustomFields(prev => [...prev, response.data]);
      setFieldDialogOpen(false);
      resetFieldForm();
      toast.success('Custom field created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create custom field');
    }
  };

  const handleUpdateField = async () => {
    if (!editingField) return;
    
    try {
      const response = await customFieldsAPI.update(editingField.id, {
        name: newField.name,
        field_type: newField.field_type,
        options: newField.field_type === 'dropdown' ? newField.options : null,
        required: newField.required,
        description: newField.description
      });
      setCustomFields(prev => prev.map(f => f.id === editingField.id ? response.data : f));
      setFieldDialogOpen(false);
      setEditingField(null);
      resetFieldForm();
      toast.success('Custom field updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update custom field');
    }
  };

  const handleDeleteField = async (fieldId) => {
    try {
      await customFieldsAPI.delete(fieldId);
      setCustomFields(prev => prev.filter(f => f.id !== fieldId));
      toast.success('Custom field deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete custom field');
    }
  };

  const openEditDialog = (field) => {
    setEditingField(field);
    setNewField({
      name: field.name,
      field_type: field.field_type,
      options: field.options || [],
      required: field.required,
      description: field.description || ''
    });
    setFieldDialogOpen(true);
  };

  const resetFieldForm = () => {
    setNewField({
      name: '',
      field_type: 'text',
      options: [],
      required: false,
      description: ''
    });
    setOptionInput('');
    setEditingField(null);
  };

  const addOption = () => {
    if (optionInput.trim() && !newField.options.includes(optionInput.trim())) {
      setNewField(prev => ({
        ...prev,
        options: [...prev.options, optionInput.trim()]
      }));
      setOptionInput('');
    }
  };

  const removeOption = (option) => {
    setNewField(prev => ({
      ...prev,
      options: prev.options.filter(o => o !== option)
    }));
  };

  const getFieldTypeIcon = (type) => {
    switch (type) {
      case 'text': return <Type className="w-4 h-4" />;
      case 'number': return <Hash className="w-4 h-4" />;
      case 'dropdown': return <List className="w-4 h-4" />;
      case 'date': return <Calendar className="w-4 h-4" />;
      default: return <Type className="w-4 h-4" />;
    }
  };

  // Backup & Restore functions
  const backupInputRef = useRef(null);
  const [exportingBackup, setExportingBackup] = useState(false);
  const [importingBackup, setImportingBackup] = useState(false);
  
  // Migration functions
  const [migratingWO, setMigratingWO] = useState(false);
  
  const handleMigrateWorkOrderNumbers = async () => {
    setMigratingWO(true);
    try {
      const response = await migrationAPI.migrateWorkOrderNumbers();
      const result = response.data;
      if (result.updated > 0) {
        toast.success(`Successfully assigned ${result.updated} work order numbers! Highest: ${result.highest_number}`);
      } else {
        toast.info(result.message || 'All work orders already have numbers assigned');
      }
    } catch (err) {
      toast.error('Failed to migrate work order numbers');
      console.error(err);
    } finally {
      setMigratingWO(false);
    }
  };

  const handleExportBackup = async () => {
    setExportingBackup(true);
    try {
      const response = await backupAPI.export();
      const backup = response.data;
      
      // Create downloadable file
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workstreampro-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Backup exported! Contains ${backup.users?.length || 0} users, ${backup.tasks?.length || 0} work orders`);
    } catch (err) {
      toast.error('Failed to export backup');
      console.error(err);
    } finally {
      setExportingBackup(false);
    }
  };

  const handleImportBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportingBackup(true);
    try {
      const text = await file.text();
      const backup = JSON.parse(text);
      
      // Validate backup structure
      if (!backup.version || !backup.exported_at) {
        throw new Error('Invalid backup file format');
      }
      
      const response = await backupAPI.import(backup);
      const result = response.data;
      
      toast.success(
        `Import complete! Added: ${result.imported.users} users, ${result.imported.tasks} work orders, ${result.imported.categories} categories, ${result.imported.places} locations`
      );
      
      if (result.imported.users > 0) {
        toast.info("Imported users have temporary password 'Welcome123'");
      }
    } catch (err) {
      if (err.message === 'Invalid backup file format') {
        toast.error('Invalid backup file. Please select a valid WorkStream Pro backup.');
      } else {
        toast.error('Failed to import backup');
      }
      console.error(err);
    } finally {
      setImportingBackup(false);
      if (backupInputRef.current) {
        backupInputRef.current.value = '';
      }
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await updateBranding(settings);
    if (result.success) {
      toast.success('Settings saved successfully');
      // Reload the page to apply changes
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      toast.error(result.error);
    }
    setSaving(false);
  };

  const handleReset = () => {
    setSettings(branding);
    toast.info('Changes reverted');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file, 200, 0.8);
      handleChange('logo_url', compressed);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Failed to process image');
    }
  };

  const handleRemoveLogo = () => {
    handleChange('logo_url', '');
  };

  // Export settings to JSON file
  const handleExportSettings = () => {
    try {
      const exportData = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        settings: settings
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workstreampro-settings-${new Date().toISOString().split('T')[0]}.json`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Settings exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export settings');
    }
  };

  // Import settings from JSON file
  const handleImportSettings = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);
      
      // Validate the import data
      if (!importData.settings) {
        throw new Error('Invalid settings file format');
      }
      
      // Apply imported settings
      setSettings(importData.settings);
      toast.success('Settings imported. Click "Save Changes" to apply.');
    } catch (err) {
      console.error('Import error:', err);
      toast.error('Failed to import settings. Please check the file format.');
    }
    
    // Reset the input
    if (importInputRef.current) {
      importInputRef.current.value = '';
    }
  };

  if (brandingLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
              <Settings className="w-10 h-10 text-amber-500" />
              Settings & Branding
            </h1>
            <p className="text-slate-400 mt-1">Customize your app's appearance</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {/* Export Button */}
            <Button 
              onClick={handleExportSettings}
              className="btn-secondary flex items-center gap-2"
              data-testid="export-settings-btn"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
            
            {/* Import Button */}
            <Button 
              onClick={() => importInputRef.current?.click()}
              className="btn-secondary flex items-center gap-2"
              data-testid="import-settings-btn"
            >
              <FileUp className="w-4 h-4" />
              Import
            </Button>
            <input
              ref={importInputRef}
              type="file"
              accept=".json"
              onChange={handleImportSettings}
              className="hidden"
            />
            
            {hasChanges && (
              <Button 
                onClick={handleReset}
                className="btn-secondary flex items-center gap-2"
                data-testid="reset-btn"
              >
                <RotateCcw className="w-4 h-4" />
                Revert
              </Button>
            )}
            <Button 
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="btn-primary flex items-center gap-2"
              data-testid="save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Live Preview */}
        <div className="card-industrial border-amber-500/30">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-amber-500" />
            <h2 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white">
              Live Preview
            </h2>
          </div>
          <div 
            className="p-6 rounded-sm border border-slate-600"
            style={{ backgroundColor: settings.background_color }}
          >
            <div 
              className="flex items-center gap-3 p-4 rounded-sm"
              style={{ backgroundColor: settings.card_color }}
            >
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="w-10 h-10 rounded-sm object-contain" />
              ) : (
                <div 
                  className="w-10 h-10 rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: settings.primary_color }}
                >
                  <Wrench className="w-5 h-5 text-white" />
                </div>
              )}
              {settings.show_logo_text && (
                <span 
                  className="text-xl font-bold uppercase tracking-wider"
                  style={{ 
                    fontFamily: settings.font_family,
                    color: settings.text_color 
                  }}
                >
                  {settings.app_name}
                </span>
              )}
            </div>
            <div className="flex gap-3 mt-4">
              <button 
                className="px-4 py-2 rounded-sm text-white font-bold uppercase text-sm"
                style={{ backgroundColor: settings.primary_color }}
              >
                Primary Button
              </button>
              <button 
                className="px-4 py-2 rounded-sm font-bold uppercase text-sm"
                style={{ 
                  backgroundColor: settings.secondary_color + '20',
                  color: settings.secondary_color,
                  border: `1px solid ${settings.secondary_color}50`
                }}
              >
                Secondary
              </button>
              <button 
                className="px-4 py-2 rounded-sm font-bold uppercase text-sm"
                style={{ 
                  backgroundColor: settings.accent_color + '20',
                  color: settings.accent_color 
                }}
              >
                Accent
              </button>
            </div>
          </div>
        </div>

        {/* Settings Tabs */}
        <Tabs defaultValue="branding" className="w-full">
          <TabsList className="bg-slate-900 border border-slate-700">
            <TabsTrigger value="branding" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <Type className="w-4 h-4 mr-2" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="colors" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <Palette className="w-4 h-4 mr-2" />
              Colors
            </TabsTrigger>
            <TabsTrigger value="logo" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <ImageIcon className="w-4 h-4 mr-2" />
              Logo
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <ToggleLeft className="w-4 h-4 mr-2" />
              Features
            </TabsTrigger>
            <TabsTrigger value="customfields" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <ClipboardList className="w-4 h-4 mr-2" />
              Custom Fields
            </TabsTrigger>
            <TabsTrigger value="backup" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <Database className="w-4 h-4 mr-2" />
              Backup
            </TabsTrigger>
            <TabsTrigger value="advanced" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <Code className="w-4 h-4 mr-2" />
              Advanced
            </TabsTrigger>
          </TabsList>

          {/* Branding Tab */}
          <TabsContent value="branding" className="mt-6 space-y-6">
            <div className="card-industrial">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white mb-4">
                App Identity
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">App Name</Label>
                  <Input
                    value={settings.app_name}
                    onChange={(e) => handleChange('app_name', e.target.value)}
                    className="input-industrial mt-1"
                    placeholder="Your App Name"
                    data-testid="app-name-input"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">Font Family</Label>
                  <select
                    value={settings.font_family}
                    onChange={(e) => handleChange('font_family', e.target.value)}
                    className="w-full mt-1 h-12 px-4 bg-slate-950 border border-slate-700 rounded-sm text-white focus:border-amber-500 focus:outline-none"
                    data-testid="font-select"
                  >
                    {FONT_OPTIONS.map(font => (
                      <option key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-sm">
                  <div>
                    <p className="text-white font-medium">Show App Name</p>
                    <p className="text-slate-400 text-sm">Display app name next to logo</p>
                  </div>
                  <Switch
                    checked={settings.show_logo_text}
                    onCheckedChange={(checked) => handleChange('show_logo_text', checked)}
                    data-testid="show-logo-text-switch"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-sm">
                  <div>
                    <p className="text-white font-medium">Dark Mode</p>
                    <p className="text-slate-400 text-sm">Use dark color scheme</p>
                  </div>
                  <Switch
                    checked={settings.dark_mode}
                    onCheckedChange={(checked) => handleChange('dark_mode', checked)}
                    data-testid="dark-mode-switch"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Colors Tab */}
          <TabsContent value="colors" className="mt-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Primary Color */}
              <div className="card-industrial">
                <Label className="text-slate-400 uppercase text-xs tracking-wider">Primary Color</Label>
                <p className="text-slate-500 text-sm mb-3">Main buttons and accents</p>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="color"
                    value={settings.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="w-12 h-12 rounded-sm cursor-pointer border-0"
                    data-testid="primary-color-picker"
                  />
                  <Input
                    value={settings.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="input-industrial flex-1 font-mono"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.primary.map(color => (
                    <button
                      key={color}
                      onClick={() => handleChange('primary_color', color)}
                      className={`w-8 h-8 rounded-sm border-2 transition-all ${
                        settings.primary_color === color ? 'border-white scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Secondary Color */}
              <div className="card-industrial">
                <Label className="text-slate-400 uppercase text-xs tracking-wider">Secondary Color</Label>
                <p className="text-slate-500 text-sm mb-3">Secondary elements and highlights</p>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="color"
                    value={settings.secondary_color}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    className="w-12 h-12 rounded-sm cursor-pointer border-0"
                  />
                  <Input
                    value={settings.secondary_color}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    className="input-industrial flex-1 font-mono"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.primary.map(color => (
                    <button
                      key={color}
                      onClick={() => handleChange('secondary_color', color)}
                      className={`w-8 h-8 rounded-sm border-2 transition-all ${
                        settings.secondary_color === color ? 'border-white scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Accent Color */}
              <div className="card-industrial">
                <Label className="text-slate-400 uppercase text-xs tracking-wider">Accent Color</Label>
                <p className="text-slate-500 text-sm mb-3">Success states and CTAs</p>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="color"
                    value={settings.accent_color}
                    onChange={(e) => handleChange('accent_color', e.target.value)}
                    className="w-12 h-12 rounded-sm cursor-pointer border-0"
                  />
                  <Input
                    value={settings.accent_color}
                    onChange={(e) => handleChange('accent_color', e.target.value)}
                    className="input-industrial flex-1 font-mono"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.primary.map(color => (
                    <button
                      key={color}
                      onClick={() => handleChange('accent_color', color)}
                      className={`w-8 h-8 rounded-sm border-2 transition-all ${
                        settings.accent_color === color ? 'border-white scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Background Color */}
              <div className="card-industrial">
                <Label className="text-slate-400 uppercase text-xs tracking-wider">Background Color</Label>
                <p className="text-slate-500 text-sm mb-3">Main page background</p>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="color"
                    value={settings.background_color}
                    onChange={(e) => handleChange('background_color', e.target.value)}
                    className="w-12 h-12 rounded-sm cursor-pointer border-0"
                  />
                  <Input
                    value={settings.background_color}
                    onChange={(e) => handleChange('background_color', e.target.value)}
                    className="input-industrial flex-1 font-mono"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.background.map(color => (
                    <button
                      key={color}
                      onClick={() => handleChange('background_color', color)}
                      className={`w-8 h-8 rounded-sm border-2 transition-all ${
                        settings.background_color === color ? 'border-white scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Card Color */}
              <div className="card-industrial">
                <Label className="text-slate-400 uppercase text-xs tracking-wider">Card Color</Label>
                <p className="text-slate-500 text-sm mb-3">Cards and panels</p>
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="color"
                    value={settings.card_color}
                    onChange={(e) => handleChange('card_color', e.target.value)}
                    className="w-12 h-12 rounded-sm cursor-pointer border-0"
                  />
                  <Input
                    value={settings.card_color}
                    onChange={(e) => handleChange('card_color', e.target.value)}
                    className="input-industrial flex-1 font-mono"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.card.map(color => (
                    <button
                      key={color}
                      onClick={() => handleChange('card_color', color)}
                      className={`w-8 h-8 rounded-sm border-2 transition-all ${
                        settings.card_color === color ? 'border-white scale-110' : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Text Color */}
              <div className="card-industrial">
                <Label className="text-slate-400 uppercase text-xs tracking-wider">Text Color</Label>
                <p className="text-slate-500 text-sm mb-3">Main text color</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={settings.text_color}
                    onChange={(e) => handleChange('text_color', e.target.value)}
                    className="w-12 h-12 rounded-sm cursor-pointer border-0"
                  />
                  <Input
                    value={settings.text_color}
                    onChange={(e) => handleChange('text_color', e.target.value)}
                    className="input-industrial flex-1 font-mono"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Logo Tab */}
          <TabsContent value="logo" className="mt-6 space-y-6">
            <div className="card-industrial">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white mb-4">
                Company Logo
              </h3>
              
              <div className="flex flex-col md:flex-row gap-6">
                {/* Current Logo */}
                <div className="flex-shrink-0">
                  <p className="text-slate-400 text-sm mb-3">Current Logo</p>
                  <div className="w-32 h-32 bg-slate-700 rounded-sm flex items-center justify-center border border-slate-600">
                    {settings.logo_url ? (
                      <img 
                        src={settings.logo_url} 
                        alt="Logo" 
                        className="max-w-full max-h-full object-contain p-2"
                      />
                    ) : (
                      <div 
                        className="w-16 h-16 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: settings.primary_color }}
                      >
                        <Wrench className="w-8 h-8 text-white" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload Section */}
                <div className="flex-1">
                  <p className="text-slate-400 text-sm mb-3">Upload New Logo</p>
                  <label 
                    className="image-upload-area p-8 text-center cursor-pointer block"
                    data-testid="logo-upload-area"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-input"
                    />
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-400">Click to upload logo</p>
                    <p className="text-slate-500 text-sm mt-1">PNG, JPG, SVG (max 200x200px recommended)</p>
                  </label>
                  
                  {settings.logo_url && (
                    <Button 
                      onClick={handleRemoveLogo}
                      className="mt-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50"
                      data-testid="remove-logo-btn"
                    >
                      Remove Logo
                    </Button>
                  )}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-700">
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">Logo Text (Alt Text)</Label>
                  <Input
                    value={settings.logo_text}
                    onChange={(e) => handleChange('logo_text', e.target.value)}
                    className="input-industrial mt-1"
                    placeholder="Company Logo"
                    data-testid="logo-text-input"
                  />
                  <p className="text-slate-500 text-sm mt-1">Alternative text for accessibility</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced" className="mt-6 space-y-6">
            <div className="card-industrial">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white mb-4">
                Custom CSS
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Add custom CSS to further customize the appearance. Use with caution.
              </p>
              <Textarea
                value={settings.custom_css}
                onChange={(e) => handleChange('custom_css', e.target.value)}
                className="input-industrial min-h-[200px] font-mono text-sm"
                placeholder={`/* Custom CSS */\n.btn-primary {\n  border-radius: 20px;\n}`}
                data-testid="custom-css-input"
              />
              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                <p className="text-yellow-400 text-sm flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  CSS is applied in real-time. Make sure to test thoroughly.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="mt-6 space-y-6">
            <div className="card-industrial">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white mb-4">
                Feature Toggles
              </h3>
              <p className="text-slate-400 text-sm mb-6">
                Enable or disable specific features in your application.
              </p>
              
              <div className="space-y-6">
                {/* Cost Tracking Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-sm border border-slate-600">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Cost Tracking</p>
                      <p className="text-slate-400 text-sm">Track labor hours, parts costs, and total costs per work order</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.feature_cost_tracking ?? true}
                    onCheckedChange={(checked) => handleChange('feature_cost_tracking', checked)}
                    data-testid="toggle-cost-tracking"
                  />
                </div>

                {/* Document Attachments Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-sm border border-slate-600">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center">
                      <FileText className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Document Attachments</p>
                      <p className="text-slate-400 text-sm">Allow uploading PDFs, images, and other files to work orders</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.feature_documents ?? true}
                    onCheckedChange={(checked) => handleChange('feature_documents', checked)}
                    data-testid="toggle-documents"
                  />
                </div>

                {/* Technician Dashboard Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-sm border border-slate-600">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Technician Dashboard</p>
                      <p className="text-slate-400 text-sm">Show "My Work" dashboard for technicians with their assigned work orders</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.feature_technician_dashboard ?? true}
                    onCheckedChange={(checked) => handleChange('feature_technician_dashboard', checked)}
                    data-testid="toggle-technician-dashboard"
                  />
                </div>

                {/* Custom Fields Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-sm border border-slate-600">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-sm flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Custom Fields</p>
                      <p className="text-slate-400 text-sm">Add custom data fields to work orders (Serial numbers, warranty status, etc.)</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.feature_custom_fields ?? true}
                    onCheckedChange={(checked) => handleChange('feature_custom_fields', checked)}
                    data-testid="toggle-custom-fields"
                  />
                </div>
              </div>

              <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                <p className="text-amber-400 text-sm">
                  Note: Changes will take effect after saving. Users may need to refresh their browser to see updated features.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Custom Fields Tab */}
          <TabsContent value="customfields" className="mt-6 space-y-6">
            <div className="card-industrial">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white">
                    Custom Fields
                  </h3>
                  <p className="text-slate-400 text-sm mt-1">
                    Add custom data fields to work orders
                  </p>
                </div>
                <Dialog open={fieldDialogOpen} onOpenChange={(open) => {
                  setFieldDialogOpen(open);
                  if (!open) resetFieldForm();
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-primary flex items-center gap-2"
                      data-testid="add-custom-field-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Add Field
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-white">
                        {editingField ? 'Edit Custom Field' : 'Add Custom Field'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="text-slate-400 uppercase text-xs tracking-wider">Field Name *</Label>
                        <Input
                          value={newField.name}
                          onChange={(e) => setNewField(prev => ({ ...prev, name: e.target.value }))}
                          className="input-industrial mt-1"
                          placeholder="e.g., Equipment Serial Number"
                          data-testid="field-name-input"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-slate-400 uppercase text-xs tracking-wider">Field Type</Label>
                        <Select 
                          value={newField.field_type} 
                          onValueChange={(value) => setNewField(prev => ({ ...prev, field_type: value }))}
                        >
                          <SelectTrigger className="input-industrial mt-1" data-testid="field-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="number">Number</SelectItem>
                            <SelectItem value="dropdown">Dropdown</SelectItem>
                            <SelectItem value="date">Date</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {newField.field_type === 'dropdown' && (
                        <div>
                          <Label className="text-slate-400 uppercase text-xs tracking-wider">Options</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              value={optionInput}
                              onChange={(e) => setOptionInput(e.target.value)}
                              className="input-industrial flex-1"
                              placeholder="Add an option..."
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                            />
                            <Button 
                              type="button"
                              onClick={addOption}
                              className="btn-secondary"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          {newField.options.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {newField.options.map((option, idx) => (
                                <span 
                                  key={`${option}-${idx}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-slate-700 text-slate-300 text-sm rounded"
                                >
                                  {option}
                                  <button
                                    type="button"
                                    onClick={() => removeOption(option)}
                                    className="text-slate-500 hover:text-red-400"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <div>
                        <Label className="text-slate-400 uppercase text-xs tracking-wider">Description</Label>
                        <Input
                          value={newField.description}
                          onChange={(e) => setNewField(prev => ({ ...prev, description: e.target.value }))}
                          className="input-industrial mt-1"
                          placeholder="Optional description or help text"
                        />
                      </div>

                      <div className="flex items-center gap-3">
                        <Switch
                          checked={newField.required}
                          onCheckedChange={(checked) => setNewField(prev => ({ ...prev, required: checked }))}
                          data-testid="field-required-switch"
                        />
                        <Label className="text-slate-400 text-sm">Required field</Label>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button 
                        variant="outline" 
                        onClick={() => { setFieldDialogOpen(false); resetFieldForm(); }}
                        className="btn-secondary"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={editingField ? handleUpdateField : handleCreateField}
                        className="btn-primary"
                        data-testid="save-field-btn"
                      >
                        {editingField ? 'Update' : 'Create'} Field
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {loadingFields ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : customFields.length > 0 ? (
                <div className="space-y-3">
                  {customFields.map((field) => (
                    <div 
                      key={field.id}
                      className="flex items-center justify-between p-4 bg-slate-700/50 rounded-sm border border-slate-600"
                      data-testid={`custom-field-${field.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-cyan-500/20 rounded-sm flex items-center justify-center">
                          {getFieldTypeIcon(field.field_type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium"><TranslatableSpan text={field.name} /></p>
                            {field.required && (
                              <span className="text-xs text-amber-400 bg-amber-500/20 px-1.5 py-0.5 rounded">Required</span>
                            )}
                          </div>
                          <p className="text-slate-400 text-sm">
                            {field.field_type.charAt(0).toUpperCase() + field.field_type.slice(1)}
                            {field.field_type === 'dropdown' && field.options?.length > 0 && (
                              <span className="text-slate-500"> • {field.options.length} options</span>
                            )}
                          </p>
                          {field.description && (
                            <p className="text-slate-500 text-xs mt-0.5"><TranslatableSpan text={field.description} /></p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(field)}
                          className="text-slate-400 hover:text-white"
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-slate-800 border-slate-700">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">Delete Custom Field</AlertDialogTitle>
                              <AlertDialogDescription className="text-slate-400">
                                Are you sure you want to delete "{field.name}"? This will remove the field from all work orders.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteField(field.id)}
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-sm">
                  <ClipboardList className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No custom fields defined</p>
                  <p className="text-slate-500 text-sm mt-1">Click "Add Field" to create your first custom field</p>
                </div>
              )}

              <div className="mt-6 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-sm">
                <p className="text-cyan-400 text-sm">
                  Custom fields will appear on work order forms and detail pages. Values are stored with each work order.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Backup Tab */}
          <TabsContent value="backup" className="mt-6 space-y-6">
            <div className="card-industrial">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-6 h-6 text-green-400" />
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white">
                  Backup & Restore
                </h3>
              </div>
              <p className="text-slate-400 text-sm mb-6">
                Export all your data to a JSON file for safekeeping, or restore from a previous backup.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Export Section */}
                <div className="p-6 bg-slate-700/30 rounded-sm border border-slate-600">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-sm flex items-center justify-center">
                      <Download className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">Export Backup</h4>
                      <p className="text-slate-400 text-sm">Download all data as JSON</p>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Creates a backup file containing:
                  </p>
                  <ul className="text-slate-400 text-sm space-y-1 mb-4">
                    <li>• All users (without passwords)</li>
                    <li>• All work orders</li>
                    <li>• Categories & Locations</li>
                    <li>• Inventory items & Suppliers</li>
                    <li>• Inventory usage history</li>
                    <li>• Custom fields</li>
                    <li>• Time logs</li>
                    <li>• Activity logs</li>
                    <li>• Branding settings</li>
                  </ul>
                  <Button
                    onClick={handleExportBackup}
                    disabled={exportingBackup}
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    data-testid="export-backup-btn"
                  >
                    {exportingBackup ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Export Backup
                      </>
                    )}
                  </Button>
                </div>

                {/* Import Section */}
                <div className="p-6 bg-slate-700/30 rounded-sm border border-slate-600">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-sm flex items-center justify-center">
                      <Upload className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">Restore Backup</h4>
                      <p className="text-slate-400 text-sm">Import data from JSON file</p>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Restores data from a backup file:
                  </p>
                  <ul className="text-slate-400 text-sm space-y-1 mb-4">
                    <li>• Merges with existing data</li>
                    <li>• Skips duplicates (by ID/email)</li>
                    <li>• Imported users get password: <code className="text-amber-400">Welcome123</code></li>
                  </ul>
                  <input
                    ref={backupInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleImportBackup}
                    className="hidden"
                  />
                  <Button
                    onClick={() => backupInputRef.current?.click()}
                    disabled={importingBackup}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="import-backup-btn"
                  >
                    {importingBackup ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import Backup
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 text-sm font-semibold">Important</p>
                  <p className="text-amber-400/80 text-sm mt-1">
                    Export your data regularly! Preview environments may reset unexpectedly. 
                    We recommend exporting before ending your session and after making important changes.
                  </p>
                </div>
              </div>

              {/* Data Migration Section */}
              <div className="mt-8 pt-6 border-t border-slate-700">
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white flex items-center gap-2 mb-4">
                  <Hash className="w-5 h-5 text-cyan-400" />
                  Data Migration
                </h3>
                
                <div className="card-industrial">
                  <h4 className="text-white font-semibold mb-2">Work Order Numbers</h4>
                  <p className="text-slate-400 text-sm mb-4">
                    Assign sequential work order numbers (WO-00001, WO-00002, etc.) to any existing work orders that don't have them.
                    This is useful after importing data or for work orders created before this feature was added.
                  </p>
                  <Button
                    onClick={handleMigrateWorkOrderNumbers}
                    disabled={migratingWO}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    data-testid="migrate-wo-numbers-btn"
                  >
                    {migratingWO ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Migrating...
                      </>
                    ) : (
                      <>
                        <Hash className="w-4 h-4 mr-2" />
                        Assign Work Order Numbers
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SettingsPage;
